import React, { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { rowsToCSV } from '@/utils/trafficking'

export default function TraffickingPreview() {
  const navigate = useNavigate()
  const location = useLocation() as any
  const headers: string[] = location?.state?.headers || []
  const rows: any[] = location?.state?.rows || []
  const assets: any[] = location?.state?.assets || []
  const campaignName: string = location?.state?.campaignName || 'campaign'

  const csv = useMemo(() => rowsToCSV(headers, rows), [headers, rows])

  if (!headers.length || !rows.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Trafficking Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">No preview data available. Return to the builder and click Preview Trafficking.</p>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/campaign-builder')}>Back to Builder</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleDownload = () => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `${campaignName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_trafficking.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Derive sheet-style fields from first row
  const first = rows[0] || {}
  const country = first['Country'] || 'USA'
  const advertiser = first['Advertiser ID'] || '—'

  const getPreview = (idx: number) => {
    const r = rows[idx] || {}
    const a = assets[idx] || {}
    const existing = r['HTML5 Preview Frames'] || a.thumbnailLink
    if (existing) return String(existing)
    if ((a.mimeType || '').startsWith('video/')) {
      const canvas = document.createElement('canvas')
      canvas.width = 240; canvas.height = 150
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#1f2937'
      ctx.fillRect(0,0,240,150)
      ctx.fillStyle = '#ffffff'
      ctx.globalAlpha = 0.9
      ctx.beginPath(); ctx.moveTo(96,60); ctx.lineTo(168,75); ctx.lineTo(96,90); ctx.closePath(); ctx.fill()
      ctx.globalAlpha = 1
      ctx.font = '10px Inter, system-ui'
      ctx.fillText('Video preview', 10, 140)
      return canvas.toDataURL('image/png')
    }
    return ''
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Display Creative Matrix PREVIEW</h1>
          <p className="text-sm text-gray-600">{rows.length} assets • Campaign: {campaignName || '—'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/campaign-builder')}>Back</Button>
          <Button onClick={handleDownload}>Download CSV</Button>
        </div>
      </div>

      {/* Phase bars */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="bg-gray-100 border rounded px-3 py-2">Phase 1: Initial Known Campaign Inputs &amp; Values Needed to Inform Creative Taxonomy</div>
        <div className="bg-indigo-100 border rounded px-3 py-2">Phase 2: Tracking Inputs (post Creative Taxonomy Generation)</div>
        <div className="bg-green-100 border rounded px-3 py-2">Phase 3: Final Inputs (post AFU)</div>
      </div>

      {/* Country / Advertiser row */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="col-span-2">
          <CardHeader className="py-2">
            <CardTitle className="text-sm">Country</CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="w-52">
              <div className="border rounded px-2 py-1 text-sm bg-white">{country}</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="py-2">
            <CardTitle className="text-sm">Advertiser Name/ID</CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="border rounded px-2 py-1 text-sm bg-white truncate" title={advertiser}>{advertiser}</div>
          </CardContent>
        </Card>
      </div>

      {/* Creative Inventory-like row */}
      <div className="border rounded overflow-hidden">
        <div className="bg-slate-700 text-white text-xs font-semibold px-3 py-2">Creative Inventory</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 border-b">
                <th className="p-2 text-left">Concept Number</th>
                <th className="p-2 text-left">Creative Status</th>
                <th className="p-2 text-left">Activation Team</th>
                <th className="p-2 text-left">Customer Type</th>
                <th className="p-2 text-left">Language</th>
                <th className="p-2 text-left">CTA/Button Description</th>
                <th className="p-2 text-left">URL</th>
                <th className="p-2 text-left">CTA 2</th>
                <th className="p-2 text-left">URL 2</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">Example</td>
                  <td className="p-2">{r['Creative Status'] || 'Adding'}</td>
                  <td className="p-2">{r['Activation Team'] || 'Programmatic'}</td>
                  <td className="p-2">{r['Customer Type']}</td>
                  <td className="p-2">{r['Language']}</td>
                  <td className="p-2">{r['CTA/Button Description']}</td>
                  <td className="p-2 text-blue-600 underline truncate max-w-[280px]" title={r['URL']}>{r['URL']}</td>
                  <td className="p-2">{r['CTA 2']}</td>
                  <td className="p-2 text-blue-600 underline truncate max-w-[220px]" title={r['URL 2']}>{r['URL 2']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Screenshot row like sheet */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 overflow-x-auto">
        {rows.slice(0,4).map((r, i) => (
          <div key={i} className="bg-green-100/50 border border-green-300 rounded p-2 flex items-center justify-center h-48">
            {(() => {
              const src = getPreview(i)
              return src ? <a href={(r['URL'] || assets[i]?.webViewLink || '#')} target="_blank" rel="noreferrer"><img src={src} alt="preview" className="h-full object-contain" /></a> : <div className="text-gray-500">No preview</div>
            })()}
          </div>
        ))}
      </div>

      {/* Creative Type / CoLab naming section */}
      <div className="border rounded overflow-hidden">
        <div className="bg-slate-700 text-white text-xs font-semibold px-3 py-2">Creative Inventory (Details)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 border-b">
                <th className="p-2 text-left">Creative Type</th>
                <th className="p-2 text-left">FDA Filing Number</th>
                <th className="p-2 text-left">Creative Agency</th>
                <th className="p-2 text-left">Campaign Type</th>
                <th className="p-2 text-left">Creative Campaign Name</th>
                <th className="p-2 text-left">Creative Content Name</th>
                <th className="p-2 text-left">Version</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{r['Channel'] === 'Display' ? 'HTML5 Banner (HTML BAN)' : r['Channel']}</td>
                  <td className="p-2">{r['FDA Filing Number']}</td>
                  <td className="p-2">{r['Creative Agency']}</td>
                  <td className="p-2">{r['Campaign Type']}</td>
                  <td className="p-2">{campaignName}</td>
                  <td className="p-2">{r['Creative Name']}</td>
                  <td className="p-2">{r['Creative Version']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Sizes & CoLab naming */}
        <div className="bg-pink-50 text-pink-700 text-xs px-3 py-2 border-t">FLAG! This should be a hard code COPY &amp; PASTE from the Taxonomy String generated within Creative Manager</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-100 border-b">
                <th className="p-2 text-left">Creative Size</th>
                <th className="p-2 text-left">CoLab CfID</th>
                <th className="p-2 text-left">CoLab Creative Name</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{r['Creative Size']}</td>
                  <td className="p-2 text-gray-500">#VALUE!</td>
                  <td className="p-2 font-mono text-[11px]">{r['Taxonomy String']}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}


